// @ts-nocheck
import React, { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import Button from "react-bootstrap/Button"
import Dropdown from "react-bootstrap/Dropdown"
import Form from "react-bootstrap/Form"
import Modal from "react-bootstrap/Modal"
import Spinner from "react-bootstrap/Spinner"
import { DateTime } from "luxon"
import { X } from "lucide-react"
import Toast from "../common/Toast.jsx"
import V2PageContainer from "../common/V2PageContainer.jsx"
import DateInput from "../common/DateInput.jsx"
import ScrollToTopButton from "../common/ScrollToTopButton.jsx"
import "./orders.css"
import useInfiniteApiList from "../hooks/useInfiniteApiList.js"
import useIsMobileViewport from "../hooks/useIsMobileViewport.js"
import useNotImplementedToast from "../hooks/useNotImplementedToast.js"
import { FIT_PER_PAGE_STORAGE_KEY, readCachedFitPerPage, writeCachedFitPerPage } from "../utils/fitPerPageStorage.js"
import { readCurrentYearFilterOn, writeCurrentYearFilterOn } from "../utils/currentYearFilterPref.js"
import { refetchRemainingMs } from "../utils/loading.js"
import { getLocationShortLabel } from "../utils/locations.js"
import OrdersDesktopTable from "./OrdersDesktopTable.jsx"
import OrdersFilters from "./OrdersFilters.jsx"
import OrdersHeader from "./OrdersHeader.jsx"
import OrdersMobileList from "./OrdersMobileList.jsx"
import OrdersBulkActions from "./OrdersBulkActions.jsx"
import {
    isOrdersPageTestModeEnabled,
    mockOrdersPageGetOrders,
    ORDERS_PAGE_TEST_LOCATIONS,
    ORDERS_PAGE_TEST_ORDERS,
} from "./ordersPageTestData.js"
import {
    buildOrderFilterChipForColumn,
    enrichOrderSearchChips,
    orderChipsToApiFilters,
    parseSearchString,
} from "../filters/index.js"

const MOBILE_PAGE_SIZE = 25

/**
 * @param {{
 *   app: any,
 *   api: any,
 *   helpers?: any,
 *   showCloneOrders?: boolean,
 *   showBasicOrderButton?: boolean,
 *   showAllLocationsBadge?: boolean,
 *   enableLocationFilter?: boolean,
 *   showCurrentYearFilter?: boolean,
 *   defaultCurrentYearFilterOn?: boolean | undefined,
 *   injectBootstrap?: boolean,
 *   enableActionsDropdown?: boolean,
 *   enableBulkConfirm?: boolean,
 *   enableBulkConfirmEmails?: boolean,
 *   disableConfirmWhenOffline?: boolean,
 *   renderActionsDropdown?: ((args: {
 *     selectedOrders: any[],
 *     selectedCount: number,
 *     app: any,
 *     api: any,
 *     helpers: any,
 *     refetchOrdersList: () => void,
 *   }) => React.ReactNode) | undefined,
 *   belowHeaderSlot?: (React.ReactNode | ((args: {
 *     app: any,
 *     api: any,
 *     helpers: any,
 *     selectedOrders: any[],
 *     selectedCount: number,
 *     mainSelectedOrders: any[],
 *     offlineSelectedOrders: any[],
 *     setOfflineSelectedOrders: (next: any[] | ((prev: any[]) => any[])) => void,
 *     refetchOrdersList: () => void,
 *   }) => React.ReactNode)),
 *   desktopColumns?: any[] | undefined,
 *   onCurrentYearToggled?: ((nextIsOn: boolean) => void) | undefined,
 *   currentYearFilterStorageKey?: string | undefined,
 *   testMode?: boolean | undefined,
 * }} props
 */
export default function OrdersPage({
    app,
    api,
    helpers,
    showCloneOrders = false,
    showBasicOrderButton = true,
    showAllLocationsBadge = true,
    enableLocationFilter = true,
    showCurrentYearFilter = false,
    defaultCurrentYearFilterOn = undefined,
    injectBootstrap = false,
    disableOfflineDatePresets = false,
    enableActionsDropdown = false,
    enableBulkConfirm = true,
    enableBulkConfirmEmails = true,
    disableConfirmWhenOffline = false,
    renderActionsDropdown = undefined,
    belowHeaderSlot = null,
    desktopColumns = undefined,
    onCurrentYearToggled = undefined,
    currentYearFilterStorageKey = undefined,
    testMode = undefined,
}) {
    const useTestMode = isOrdersPageTestModeEnabled(testMode)
    const effectiveApp = useTestMode ? {
        ...app,
        loaded: true,
        locations: ORDERS_PAGE_TEST_LOCATIONS,
        locationId: app?.locationId ?? ORDERS_PAGE_TEST_LOCATIONS[0]?.id,
    } : app
    const { toastProps, showNotImplementedToast } = useNotImplementedToast()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [orders, setOrders] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [isLoadingInitialDesktop, setIsLoadingInitialDesktop] = useState(true)
    const [showDesktopRefetchLoader, setShowDesktopRefetchLoader] = useState(false)
    const [showCustomDateModal, setShowCustomDateModal] = useState(false)
    const [customStartDate, setCustomStartDate] = useState("")
    const [customEndDate, setCustomEndDate] = useState("")
    const [refetchToken, setRefetchToken] = useState(0)
    const [fitPerPage, setFitPerPage] = useState(
        () => readCachedFitPerPage(FIT_PER_PAGE_STORAGE_KEY.orders) ?? 25
    )
    const hasLoadedDesktopOnceRef = React.useRef(false)
    const desktopRequestIdRef = React.useRef(0)
    const desktopFetchInFlightRef = React.useRef(0)
    const searchBarAddChipRef = React.useRef(/** @type {((chip: any) => void) | null} */(null))
    const isMobile = useIsMobileViewport()
    const pageParam = searchParams.get("page")
    const perPageParam = searchParams.get("per_page")
    const sortByParam = searchParams.get("sort_by")
    const sortDirParam = searchParams.get("sort_dir")
    const searchParam = searchParams.get("search")
    const locationParam = searchParams.get("location")
    const startDateParam = searchParams.get("start_date")
    const endDateParam = searchParams.get("end_date")
    const yearParam = searchParams.get("year")
    const customerIdParam = searchParams.get("customer_id")

    const [orderTemplates, setOrderTemplates] = useState(null) // null = loading, [] = loaded empty
    const [deletingTemplateId, setDeletingTemplateId] = useState(null)
    const [showAddTemplateModal, setShowAddTemplateModal] = useState(false)
    const [addTemplateMasterNum, setAddTemplateMasterNum] = useState("")
    const [addTemplateName, setAddTemplateName] = useState("")
    const [addTemplateLoading, setAddTemplateLoading] = useState(false)
    const [addTemplateError, setAddTemplateError] = useState("")
    const [cloningOrder, setCloningOrder] = useState(false)
    const [selectedOrders, setSelectedOrders] = useState(/** @type {any[]} */ ([]))
    const [offlineSelectedOrders, setOfflineSelectedOrders] = useState(/** @type {any[]} */ ([]))

    const formatStatus = helpers?.formatStatus ?? ((x) => String(x ?? ""))
    const getCurrentLocation = helpers?.getCurrentLocation ?? (() => null)
    const lastNameCommaFirstName = helpers?.lastNameCommaFirstName ?? (() => "")

    function getPage() {
        const parsed = Number(pageParam)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
    }

    function getPerPage() {
        if (!perPageParam || (perPageParam ?? "").toLowerCase() === "fit") {
            return fitPerPage
        }
        const parsed = Number(perPageParam)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : 25
    }

    function isPerPageFit() {
        return !perPageParam || (perPageParam ?? "").toLowerCase() === "fit"
    }

    function handlePageChange(nextPage) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set("page", String(nextPage))
        setSearchParams(nextParams)
    }

    function handlePerPageChange(nextPerPage) {
        const nextParams = new URLSearchParams(searchParams)
        if (nextPerPage === "fit") {
            nextParams.set("per_page", "fit")
        } else {
            nextParams.set("per_page", String(nextPerPage))
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function handleFitPerPageChange(nextFitPerPage) {
        setFitPerPage((current) => {
            if (!Number.isInteger(nextFitPerPage) || nextFitPerPage <= 0) {
                return current
            }
            if (current === nextFitPerPage) {
                return current
            }
            writeCachedFitPerPage(FIT_PER_PAGE_STORAGE_KEY.orders, nextFitPerPage)
            return nextFitPerPage
        })
    }

    function getSearchText() {
        return parseSearchString(searchParam ?? "").freeText
    }

    const parsedSearch = React.useMemo(
        () => parseSearchString(searchParam ?? ""),
        [searchParam]
    )

    const searchChips = React.useMemo(
        () => enrichOrderSearchChips(parsedSearch.chips, {
            locations: effectiveApp?.locations ?? [],
            statusOptions: helpers?.statusOptions,
        }),
        [parsedSearch.chips, effectiveApp?.locations, helpers?.statusOptions]
    )

    const activeFilterLabels = React.useMemo(
        () => new Set(parsedSearch.chips.map((chip) => chip.label)),
        [parsedSearch.chips]
    )

    function getSortBy() {
        return sortByParam ?? "orderDate"
    }

    function getSortDirection() {
        return sortDirParam === "asc" ? "asc" : "desc"
    }

    function getSortField(sortBy) {
        switch (sortBy) {
            case "master":
                return "master"
            case "status":
                return "status"
            case "firstName":
                return "ship_first_name"
            case "lastName":
                return "ship_last_name"
            case "fishTag":
                return "fish_tag"
            case "tripCode":
                return "trip_code"
            case "sjNum":
                return "st_jeans_num"
            case "orderDate":
            default:
                return "ordered_at"
        }
    }

    function getStartDate() {
        return startDateParam ?? ""
    }

    function getEndDate() {
        return endDateParam ?? ""
    }

    function getCurrentYear() {
        return String(new Date().getFullYear())
    }

    function getYear() {
        return yearParam ?? ""
    }

    function getDateXDaysAgo(x) {
        return DateTime.now().minus({ days: x }).toFormat("yyyy-MM-dd")
    }

    function datePresetSelection() {
        if (!getStartDate() && !getEndDate()) {
            return ""
        }
        if (getStartDate() === getDateXDaysAgo(6) &&
            getEndDate() === getDateXDaysAgo(0)) {
            return "last-7-days"
        }
        if (getStartDate() === getDateXDaysAgo(0) &&
            getEndDate() === getDateXDaysAgo(0)) {
            return "today"
        }
        return "custom"
    }

    function getLocationIdParameter() {
        if (locationParam && locationParam.toLowerCase() === "all") {
            return null
        }
        if (searchParam) {
            return null
        }
        return effectiveApp?.locationId
    }

    function fetchOrdersPage(page, perPage) {
        if (useTestMode) {
            return Promise.resolve(mockOrdersPageGetOrders({
                page,
                perPage,
                sortField: getSortField(getSortBy()),
                sortDirection: getSortDirection(),
                searchText: getSearchText(),
            }))
        }
        return api.getOrders(
            page,
            perPage,
            getSortField(getSortBy()),
            getSortDirection(),
            getSearchText(),
            buildFilters()
        )
    }

    useEffect(() => {
        const effectiveDefaultOn = (
            typeof defaultCurrentYearFilterOn === "boolean"
                ? defaultCurrentYearFilterOn
                : readCurrentYearFilterOn(currentYearFilterStorageKey)
        )
        if (!showCurrentYearFilter || !effectiveDefaultOn) {
            return
        }
        // Only auto-fill when the year is truly unset.
        // If the URL explicitly sets `year` (including `year=all`), never override it.
        const year = String(getYear()).trim()
        if (year !== "") {
            return
        }
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set("year", getCurrentYear())
        setSearchParams(nextParams)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCurrentYearFilter, defaultCurrentYearFilterOn, currentYearFilterStorageKey, yearParam])

    function buildFilters() {
        const filters = orderChipsToApiFilters(parsedSearch.chips)
        if (enableLocationFilter) {
            const locationIdParameter = getLocationIdParameter()
            if (locationIdParameter) {
                filters.push(`{location_id = ${locationIdParameter}}`)
            }
        }
        if (customerIdParam && /^\d+$/.test(customerIdParam)) {
            filters.push(`{customer_id = ${customerIdParam}}`)
        }
        const year = String(getYear()).trim()
        if (year && year.toLowerCase() !== "all") {
            filters.push(`{year = ${year}}`)
        }
        if (getStartDate()) {
            filters.push(`{ordered_at gte ${getStartDate()}}`)
        }
        if (getEndDate()) {
            filters.push(`{ordered_at lte ${getEndDate()}}`)
        }
        return filters
    }

    function mapOrderForList(order, locations) {
        const store = getCurrentLocation(order, locations)
        const pickUpLocation = locations.find(
            (x) => x.id === order.pick_up_location_id
        )
        const shipTo = pickUpLocation ?
            `P/U: ${pickUpLocation.pick_up_name || pickUpLocation.name}` :
            lastNameCommaFirstName(order.ship_to)

        return {
            ...order,
            location: store?.name ?? "",
            locationShort: (getLocationShortLabel(store) || store?.name) ?? "",
            master: order.master,
            status: formatStatus(order.status),
            firstName: order.ship_to?.first_name ??
                order.bill_to?.first_name,
            lastName: order.ship_to?.last_name ??
                order.bill_to?.last_name,
            fishTag: order.fish_tag ?? "",
            tripCode: order.trip_code ?? "",
            sjNum: order.st_jeans_num ?? "",
            shipTo,
            orderDate: order.ordered_at?.split("T")?.[0] ?? "",
        }
    }

    function handleSearchSubmit(searchString) {
        const nextParams = new URLSearchParams(searchParams)
        if (!searchString?.trim()) {
            nextParams.delete("search")
        } else {
            nextParams.set("search", searchString.trim())
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function onRefreshClick() {
        window.location.reload()
    }

    function refetchOrdersList() {
        setRefetchToken((current) => current + 1)
    }

    const mergedSelectedOrders = React.useMemo(() => {
        const combined = [...(offlineSelectedOrders ?? []), ...(selectedOrders ?? [])]
        const seen = new Set()
        const result = []
        for (const order of combined) {
            const id = order?.id ?? order?.orderId ?? order?.master
            if (id == null) {
                continue
            }
            if (seen.has(id)) {
                continue
            }
            seen.add(id)
            result.push(order)
        }
        return result
    }, [selectedOrders, offlineSelectedOrders])

    function handleSort(columnKey, direction) {
        const nextParams = new URLSearchParams(searchParams)
        if (direction == null) {
            nextParams.delete("sort_by")
            nextParams.delete("sort_dir")
        } else {
            nextParams.set("sort_by", columnKey)
            nextParams.set("sort_dir", direction)
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function handleAddOrderFilter(columnKey) {
        const chip = buildOrderFilterChipForColumn(columnKey, {
            locations: effectiveApp?.locations ?? [],
            statusOptions: helpers?.statusOptions,
        })
        if (!chip || !searchBarAddChipRef.current) {
            return
        }
        searchBarAddChipRef.current(chip)
    }

    const registerSearchBarAddChip = React.useCallback((addChipFn) => {
        searchBarAddChipRef.current = addChipFn
    }, [])

    function onToggleAllLocations() {
        const nextParams = new URLSearchParams(searchParams)
        if ((locationParam ?? "").toLowerCase() === "all") {
            nextParams.delete("location")
        } else {
            nextParams.set("location", "all")
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function onToggleLast7Days() {
        const nextParams = new URLSearchParams(searchParams)
        if (datePresetSelection() === "last-7-days") {
            nextParams.delete("start_date")
            nextParams.delete("end_date")
        } else {
            nextParams.set("start_date", getDateXDaysAgo(6))
            nextParams.set("end_date", getDateXDaysAgo(0))
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function onToggleToday() {
        const nextParams = new URLSearchParams(searchParams)
        if (datePresetSelection() === "today") {
            nextParams.delete("start_date")
            nextParams.delete("end_date")
        } else {
            nextParams.set("start_date", getDateXDaysAgo(0))
            nextParams.set("end_date", getDateXDaysAgo(0))
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function onToggleCurrentYear() {
        const nextParams = new URLSearchParams(searchParams)
        const isCurrentlyOn = String(getYear()) === getCurrentYear()
        const nextIsOn = !isCurrentlyOn
        if (isCurrentlyOn) {
            nextParams.delete("year")
        } else {
            nextParams.set("year", getCurrentYear())
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
        writeCurrentYearFilterOn(nextIsOn, currentYearFilterStorageKey)
        if (typeof onCurrentYearToggled === "function") {
            onCurrentYearToggled(nextIsOn)
        }
    }

    function openCustomDateRangeModal() {
        setCustomStartDate(getStartDate() || DateTime.now().toFormat("yyyy-MM-dd"))
        setCustomEndDate(getEndDate() || DateTime.now().toFormat("yyyy-MM-dd"))
        setShowCustomDateModal(true)
    }

    function applyCustomDateRange() {
        if (!customStartDate || !customEndDate) {
            return
        }
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set("start_date", customStartDate)
        nextParams.set("end_date", customEndDate)
        nextParams.set("page", "1")
        setSearchParams(nextParams)
        setShowCustomDateModal(false)
    }

    function clearDateRange() {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete("start_date")
        nextParams.delete("end_date")
        nextParams.set("page", "1")
        setSearchParams(nextParams)
        setShowCustomDateModal(false)
    }

    function handleOrderClick(order) {
        navigate(`/v2/orders/${order.id}/order`, {
            state: { order },
        })
    }

    useEffect(() => {
        if (!app?.loaded || !showCloneOrders) {
            return
        }
        api.getOrderTemplates()
            .then((templates) => setOrderTemplates(templates))
            .catch(() => setOrderTemplates([]))
    }, [app?.loaded, showCloneOrders])

    function openAddTemplateModal() {
        setAddTemplateMasterNum("")
        setAddTemplateName("")
        setAddTemplateError("")
        setAddTemplateLoading(false)
        setShowAddTemplateModal(true)
    }

    function submitAddTemplate() {
        if (!addTemplateMasterNum.trim() || !addTemplateName.trim()) {
            return
        }
        setAddTemplateLoading(true)
        setAddTemplateError("")

        const master = addTemplateMasterNum.trim()
        const name = addTemplateName.trim()

        Promise.resolve()
            .then(async () => {
                const order = await api.getOrderByMaster(master)
                if (!order?.id) {
                    throw new Error(`No order found for master #${master}.`)
                }
                return await api.createOrderTemplate({ order_id: order.id, name })
            })
            .then((template) => {
                setOrderTemplates((prev) => [...(prev ?? []), template])
                setShowAddTemplateModal(false)
            })
            .catch((e) => setAddTemplateError(e.message))
            .finally(() => setAddTemplateLoading(false))
    }

    function deleteTemplate(e, template) {
        e.stopPropagation()
        setDeletingTemplateId(template.id)
        api.deleteOrderTemplate(template.id)
            .then(() => setOrderTemplates((prev) => (prev ?? []).filter((t) => t.id !== template.id)))
            .finally(() => setDeletingTemplateId(null))
    }

    function cloneFromTemplate(template) {
        return () => {
            setCloningOrder(true)
            api.cloneOrderV2(template.order_id)
                .then(() => refetchOrdersList())
                .finally(() => setCloningOrder(false))
        }
    }

    function cloneOrderButton() {
        if (!showCloneOrders) {
            return null
        }

        const isLoading = orderTemplates === null || cloningOrder
        const sorted = orderTemplates ? [...orderTemplates].sort((a, b) => a.name.localeCompare(b.name)) : []

        return (
            <Dropdown>
                <Dropdown.Toggle
                    disabled={isLoading}
                    className={`btn btn-warning btn-sm shadow-sm clone-order-btn${isLoading ? " clone-order-loading" : ""}`}
                >
                    <span className="d-inline d-md-none">Clone</span>
                    <span className="d-none d-md-inline">Clone Order</span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item className="py-2 fw-bold" onClick={openAddTemplateModal}>
                        + Add Template
                    </Dropdown.Item>
                    {sorted.map((template) => (
                        <Dropdown.Item
                            key={template.id}
                            className="border-top py-2 d-flex align-items-center template-item"
                            onClick={cloneFromTemplate(template)}
                        >
                            <span className="template-name" title={template.name}>
                                {template.name}
                            </span>
                            {deletingTemplateId === template.id ? (
                                <Spinner size="sm" className="ms-2" />
                            ) : (
                                <button
                                    type="button"
                                    className="btn p-0 border-0 bg-transparent template-delete-btn ms-2 d-inline-flex align-items-center justify-content-center"
                                    aria-label={`Delete template ${template.name}`}
                                    title="Delete template"
                                    onClick={(e) => deleteTemplate(e, template)}
                                >
                                    <X size={16} aria-hidden />
                                </button>
                            )}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        )
    }

    const mobileOrdersList = useInfiniteApiList({
        enabled: Boolean(effectiveApp?.loaded && isMobile),
        perPage: MOBILE_PAGE_SIZE,
        deps: [
            effectiveApp?.locationId,
            sortByParam,
            sortDirParam,
            searchParam,
            locationParam,
            startDateParam,
            endDateParam,
            yearParam,
            customerIdParam,
            refetchToken,
            useTestMode,
        ],
        fetchPage: async (page, perPage) => {
            const locations = /** @type {any[]} */ (effectiveApp?.locations ?? [])
            const body = await fetchOrdersPage(page, perPage)
            return {
                items: (body?.data ?? []).map((order) => mapOrderForList(order, locations)),
                total: body?.total ?? 0,
            }
        },
    })

    useEffect(() => {
        if (!effectiveApp?.loaded) {
            return
        }
        if (isMobile) {
            return
        }

        const locations = /** @type {any[]} */ (effectiveApp?.locations ?? [])
        const isRefetch = hasLoadedDesktopOnceRef.current
        const fetchStartedAt = Date.now()

        if (!isRefetch) {
            setIsLoadingInitialDesktop(true)
        }
        if (isRefetch) {
            desktopFetchInFlightRef.current += 1
            setShowDesktopRefetchLoader(true)
        }
        const requestId = ++desktopRequestIdRef.current

        function completeDesktopRefetchSlot() {
            desktopFetchInFlightRef.current = Math.max(0, desktopFetchInFlightRef.current - 1)
            if (desktopFetchInFlightRef.current === 0) {
                setShowDesktopRefetchLoader(false)
            }
        }

        ; fetchOrdersPage(
            getPage(),
            getPerPage()
        )
            .then((body) => {
                if (requestId !== desktopRequestIdRef.current) {
                    if (isRefetch) {
                        completeDesktopRefetchSlot()
                    }
                    return
                }
                const mappedOrders = (body?.data ?? []).map((order) => (
                    mapOrderForList(order, locations)
                ))
                const total = body?.total ?? 0
                if (!isRefetch) {
                    setOrders(mappedOrders)
                    setTotalRows(total)
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== desktopRequestIdRef.current) {
                        completeDesktopRefetchSlot()
                        return
                    }
                    setOrders(mappedOrders)
                    setTotalRows(total)
                    completeDesktopRefetchSlot()
                }, delay)
            })
            .catch((e) => {
                if (requestId !== desktopRequestIdRef.current) {
                    if (isRefetch) {
                        completeDesktopRefetchSlot()
                    }
                    return
                }
                console.error("Failed to load v2 orders:", e)
                if (!isRefetch) {
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== desktopRequestIdRef.current) {
                        completeDesktopRefetchSlot()
                        return
                    }
                    completeDesktopRefetchSlot()
                }, delay)
            })
            .finally(() => {
                if (requestId !== desktopRequestIdRef.current) {
                    return
                }
                if (!hasLoadedDesktopOnceRef.current) {
                    hasLoadedDesktopOnceRef.current = true
                    setIsLoadingInitialDesktop(false)
                }
            })
    }, [effectiveApp, isMobile, pageParam, perPageParam, fitPerPage, sortByParam, sortDirParam, searchParam,
        locationParam, startDateParam, endDateParam, yearParam, customerIdParam, refetchToken, useTestMode])

    return (
        <V2PageContainer className="pb-0 pb-md-0">
            <Toast {...toastProps} />
            {useTestMode ? (
                <div
                    className="alert alert-warning py-2 px-3 mb-3 mb-md-4"
                    style={{ fontSize: "0.875rem" }}
                    role="status"
                >
                    Orders page test mode — showing {ORDERS_PAGE_TEST_ORDERS.length} hardcoded orders
                </div>
            ) : null}
            <OrdersHeader
                showBasicOrderButton={showBasicOrderButton}
                onBasicOrderClick={() => showNotImplementedToast()}
                onNewOrderClick={() => navigate("/v2/order-form")}
                actionsSlot={
                    enableActionsDropdown
                        ? typeof renderActionsDropdown === "function"
                            ? renderActionsDropdown({
                                selectedOrders: mergedSelectedOrders,
                                selectedCount: mergedSelectedOrders.length,
                                app,
                                api,
                                helpers,
                                refetchOrdersList,
                            })
                            : (
                                <OrdersBulkActions
                                    app={app}
                                    api={api}
                                    helpers={helpers}
                                    selectedOrders={mergedSelectedOrders}
                                    refetchOrdersList={refetchOrdersList}
                                    enableBulkConfirm={enableBulkConfirm}
                                    enableBulkConfirmEmails={enableBulkConfirmEmails}
                                    disableConfirmWhenOffline={disableConfirmWhenOffline}
                                    injectBootstrap={injectBootstrap}
                                />
                            )
                        : null
                }
                rightSlot={cloneOrderButton()}
            />
            {typeof belowHeaderSlot === "function"
                ? belowHeaderSlot({
                    app,
                    api,
                    helpers,
                    selectedOrders: mergedSelectedOrders,
                    selectedCount: mergedSelectedOrders.length,
                    mainSelectedOrders: selectedOrders,
                    offlineSelectedOrders,
                    setOfflineSelectedOrders,
                    refetchOrdersList,
                })
                : belowHeaderSlot}
            <OrdersFilters
                showAllLocationsBadge={showAllLocationsBadge}
                showCurrentYearFilter={showCurrentYearFilter}
                currentYearLabel={getCurrentYear()}
                searchChips={searchChips}
                searchFreeText={parsedSearch.freeText}
                onSearch={handleSearchSubmit}
                onRegisterAddChip={registerSearchBarAddChip}
                onRefresh={onRefreshClick}
                onToggleAllLocations={onToggleAllLocations}
                onToggleLast7Days={onToggleLast7Days}
                onToggleToday={onToggleToday}
                onToggleCurrentYear={onToggleCurrentYear}
                onOpenCustomDateRange={openCustomDateRangeModal}
                disableOfflineDatePresets={disableOfflineDatePresets}
                isAllLocations={(locationParam ?? "").toLowerCase() === "all"}
                isLast7Days={datePresetSelection() === "last-7-days"}
                isToday={datePresetSelection() === "today"}
                isCurrentYear={String(getYear()) === getCurrentYear()}
                isCustomDateRange={datePresetSelection() === "custom"}
            />
            {!isMobile ? (
                <div className="flex-grow-1 d-flex flex-column">
                    <OrdersDesktopTable
                        orders={orders}
                        isLoadingInitial={isLoadingInitialDesktop}
                        totalRows={totalRows}
                        page={getPage()}
                        perPage={getPerPage()}
                        onPageChange={handlePageChange}
                        onPerPageChange={handlePerPageChange}
                        perPageSelection={isPerPageFit() ? "fit" : getPerPage()}
                        onFitPerPageChange={handleFitPerPageChange}
                        onOrderClick={handleOrderClick}
                        sortBy={getSortBy()}
                        sortDirection={getSortDirection()}
                        onSort={handleSort}
                        onAddFilter={handleAddOrderFilter}
                        activeFilterLabels={activeFilterLabels}
                        showRefetchLoader={showDesktopRefetchLoader}
                        onSelectionChange={(nextSelected) => setSelectedOrders(nextSelected)}
                        columns={desktopColumns}
                    />
                </div>
            ) : (
                <>
                    <div className="mb-3">
                        <p className="text-secondary mb-0" style={{ fontSize: "0.875rem" }}>
                            Showing {mobileOrdersList.total} orders
                        </p>
                    </div>
                    <OrdersMobileList
                        orders={mobileOrdersList.items}
                        hasMore={mobileOrdersList.hasMore}
                        isLoading={mobileOrdersList.isLoading}
                        isLoadingInitial={mobileOrdersList.isLoadingInitial}
                        onLoadMore={mobileOrdersList.loadMore}
                        onOrderClick={handleOrderClick}
                        selectedOrders={selectedOrders}
                        onSelectedOrdersChange={setSelectedOrders}
                    />
                </>
            )}
            <ScrollToTopButton className="d-lg-none" />

            <Modal
                size="lg"
                show={showCustomDateModal}
                onHide={() => setShowCustomDateModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Custom Date Range</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column flex-sm-row gap-3">
                        <div className="w-100">
                            <label className="form-label" htmlFor="orders-custom-start-date">Start date</label>
                            <DateInput
                                id="orders-custom-start-date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                            />
                        </div>
                        <div className="w-100">
                            <label className="form-label" htmlFor="orders-custom-end-date">End date</label>
                            <DateInput
                                id="orders-custom-end-date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={clearDateRange}
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={applyCustomDateRange}
                    >
                        Apply
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showAddTemplateModal}
                onHide={() => setShowAddTemplateModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add New Order Template</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">
                        Specify the order to use as the basis for the template.
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Label>Master #</Form.Label>
                        <Form.Control
                            type="text"
                            value={addTemplateMasterNum}
                            onChange={(e) => setAddTemplateMasterNum(e.target.value)}
                            placeholder="e.g. M045237"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Template Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={addTemplateName}
                            onChange={(e) => setAddTemplateName(e.target.value)}
                            placeholder="e.g. Standard Frozen Order"
                        />
                    </Form.Group>
                    {addTemplateError ? (
                        <p className="text-danger mb-0">{addTemplateError}</p>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddTemplateModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={
                            addTemplateLoading ||
                            !addTemplateMasterNum.trim() ||
                            !addTemplateName.trim()
                        }
                        onClick={submitAddTemplate}
                    >
                        {addTemplateLoading ? <Spinner size="sm" /> : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </V2PageContainer>
    )
}

